import { kebabCase } from 'lodash';
import { useCallback } from 'react';
export function createFormSectionMeta(props) {
    return Object.assign({}, props);
}
export const defaultSectionTitleRegExp = /^Section \d+$/;
export function useEditFormSection(trackChanges, formState, notifyFormEdit) {
    const { insert, move, remove, update } = trackChanges;
    const getStateNodePath = useCallback((stateNode) => {
        if (!trackChanges.final.states || !stateNode) {
            throw new Error('No selected state node');
        }
        const index = trackChanges.final.states.indexOf(stateNode);
        return `states[${index}]`;
    }, [trackChanges.final.states]);
    /**
     * Add an event for a section. If no section state
     * node provided, then use selected state node.
     */
    const addEvent = useCallback((event, stateNode) => {
        var _a, _b;
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        // default transition to next state node if it exists
        const index = (_a = trackChanges.final.states) === null || _a === void 0 ? void 0 : _a.findIndex(s => s.name === stateNode.name);
        let target = '';
        if (typeof index === 'number' && index > -1) {
            const targetStateNode = (_b = trackChanges.final.states) === null || _b === void 0 ? void 0 : _b[index + 1];
            if (targetStateNode)
                target = targetStateNode.name;
        }
        if (!event)
            event = { transitions: [{ target }] };
        const fullPath = getStateNodePath(stateNode);
        if (!stateNode.events) {
            update(`${fullPath}.events`, []);
        }
        insert(`${fullPath}.events`, event);
        notifyFormEdit();
    }, [
        formState.selectedStateNode,
        trackChanges.final.states,
        getStateNodePath,
        insert,
        notifyFormEdit,
        update
    ]);
    /**
     * Update an event for a section. If no section state
     * node provided, then use selected state node.
     */
    const updateEvent = useCallback((index, event, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        const keys = Object.keys(event);
        for (const key of keys) {
            update(`${fullPath}.events[${index}].${key}`, event[key]);
        }
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, update]);
    /**
     * Reorder an event. If no section state node provided,
     * then use selected state node.
     */
    const moveEvent = useCallback((sourceIndex, destinationIndex, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        move(`${fullPath}.events`, sourceIndex, destinationIndex);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, move, notifyFormEdit]);
    /**
     * Delete an event
     */
    const deleteEvent = useCallback((index, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        remove(`${fullPath}.events[${index}]`);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, remove]);
    /**
     * Get event index from state node
     * @param event
     * @param stateNode
     */
    const getEventIndex = (event, stateNode) => {
        var _a;
        const eventIndex = (_a = stateNode.events) === null || _a === void 0 ? void 0 : _a.indexOf(event);
        if (typeof eventIndex === 'undefined' || eventIndex === -1) {
            throw new Error('Could not find event in stateNode config');
        }
        return eventIndex;
    };
    /**
     * Add a transition for an event. If no section state
     * node provided, then use selected state node.
     */
    const addTransition = useCallback((event, stateNode) => {
        var _a;
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const eventIndex = getEventIndex(event, stateNode);
        const fullPath = getStateNodePath(stateNode);
        if (!((_a = stateNode.events) === null || _a === void 0 ? void 0 : _a[eventIndex].transitions)) {
            update(`${fullPath}.events[${eventIndex}].transitions`, []);
        }
        insert(`${fullPath}.events[${eventIndex}].transitions`, { target: '' });
        notifyFormEdit();
    }, [
        formState.selectedStateNode,
        getStateNodePath,
        insert,
        update,
        notifyFormEdit
    ]);
    /**
     * Update a transition for an event. If no section state
     * node provided, then use selected state node.
     */
    const updateTransition = useCallback((event, transitionIndex, transition, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const eventIndex = getEventIndex(event, stateNode);
        const fullPath = getStateNodePath(stateNode);
        const keys = Object.keys(transition);
        for (const key of keys) {
            update(`${fullPath}.events[${eventIndex}].transitions[${transitionIndex}].${key}`, transition[key]);
        }
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, update]);
    /**
     * Reorder an event transition. If no section state node provided,
     * then use selected state node.
     */
    const moveTransition = useCallback((event, sourceIndex, destinationIndex, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        const eventIndex = getEventIndex(event, stateNode);
        move(`${fullPath}.events[${eventIndex}].transitions`, sourceIndex, destinationIndex);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, move, notifyFormEdit]);
    /**
     * Delete an event transition.
     */
    const deleteTransition = useCallback((event, transitionIndex, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const eventIndex = getEventIndex(event, stateNode);
        const fullPath = getStateNodePath(stateNode);
        remove(`${fullPath}.events[${eventIndex}].transitions[${transitionIndex}]`);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, remove]);
    /**
     * Get event index from state node
     * @param event
     * @param stateNode
     */
    const getEventTransitionIndex = (transition, event) => {
        var _a;
        const index = (_a = event.transitions) === null || _a === void 0 ? void 0 : _a.indexOf(transition);
        if (typeof index === 'undefined' || index === -1) {
            throw new Error('Could not find transition in event config');
        }
        return index;
    };
    /**
     * Add a condition for an event transition. If no section state
     * node provided, then use selected state node.
     */
    const addTransitionCondition = useCallback((event, transition, condition, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const eventIndex = getEventIndex(event, stateNode);
        const transitionIndex = getEventTransitionIndex(transition, event);
        const fullPath = getStateNodePath(stateNode);
        const path = `${fullPath}.events[${eventIndex}].transitions[${transitionIndex}].conditions`;
        if (!transition.conditions) {
            update(path, []);
        }
        insert(path, condition);
        notifyFormEdit();
    }, [
        formState.selectedStateNode,
        getStateNodePath,
        insert,
        update,
        notifyFormEdit
    ]);
    /**
     * Update a condition for an event transition. If no section state
     * node provided, then use selected state node.
     */
    const updateTransitionCondition = useCallback((event, transition, conditionIndex, condition, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const eventIndex = getEventIndex(event, stateNode);
        const transitionIndex = getEventTransitionIndex(transition, event);
        const fullPath = getStateNodePath(stateNode);
        update(`${fullPath}.events[${eventIndex}].transitions[${transitionIndex}].conditions[${conditionIndex}]`, condition);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, update]);
    /**
     * Reorder a transition condition. If no section state node provided,
     * then use selected state node.
     */
    const moveTransitionCondition = useCallback((event, transition, sourceIndex, destinationIndex, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        const eventIndex = getEventIndex(event, stateNode);
        const transitionIndex = getEventTransitionIndex(transition, event);
        move(`${fullPath}.events[${eventIndex}].transitions[${transitionIndex}].conditions`, sourceIndex, destinationIndex);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, move, notifyFormEdit]);
    /**
     * Delete an transition condition.
     */
    const deleteTransitionCondition = useCallback((event, transition, conditionIndex, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const eventIndex = getEventIndex(event, stateNode);
        const transitionIndex = getEventTransitionIndex(transition, event);
        const fullPath = getStateNodePath(stateNode);
        remove(`${fullPath}.events[${eventIndex}].transitions[${transitionIndex}].conditions[${conditionIndex}]`);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, remove]);
    /**
     * Recursively update all event targets to new state node name path
     */
    const updateAllEventTargets = useCallback((oldName, newName, stateNode) => {
        if (!stateNode)
            stateNode = trackChanges.final;
        if (stateNode.events) {
            for (const event of stateNode.events) {
                if (event.transitions) {
                    for (let i = 0; i < event.transitions.length; i++) {
                        const transition = event.transitions[i];
                        const pathArray = transition.target.split('/');
                        const index = pathArray.indexOf(oldName);
                        if (index > -1) {
                            // every stateNode must have unique name in entire tree (see getUniqueStateNodeName())
                            pathArray[index] = newName;
                            updateTransition(event, i, { target: pathArray.join('/') }, stateNode);
                        }
                    }
                }
            }
        }
        if (stateNode.states) {
            for (const childStateNode of stateNode.states) {
                updateAllEventTargets(oldName, newName, childStateNode);
            }
        }
    }, [trackChanges.final, updateTransition]);
    const updateSectionNames = useCallback(() => {
        var _a;
        (_a = trackChanges.final.states) === null || _a === void 0 ? void 0 : _a.forEach((stateNode, index) => {
            var _a, _b;
            if (((_a = stateNode.meta) === null || _a === void 0 ? void 0 : _a.title) &&
                defaultSectionTitleRegExp.test(stateNode.meta.title)) {
                const title = `Section ${index + 1}`;
                if (((_b = stateNode.meta) === null || _b === void 0 ? void 0 : _b.title) !== title) {
                    const path = getStateNodePath(stateNode);
                    update(`${path}.meta.title`, title);
                    update(`${path}.name`, kebabCase(title));
                }
            }
        });
    }, [getStateNodePath, trackChanges.final.states, update]);
    /**
     * Add a section
     */
    const addSection = useCallback((index) => {
        const title = `Section ${index + 1}`;
        const name = kebabCase(title);
        insert(`states[${index}]`, {
            name,
            meta: createFormSectionMeta({
                title,
                elements: []
            })
        });
        updateSectionNames();
        notifyFormEdit();
        // add a default transition when adding a new section
        // const sections = trackChanges.final.states;
        // const stateNode = sections?.[index - 1];
        // if (index > 0 && stateNode) {
        //   addEvent(
        //     { name: 'Submit', transitions: [{ target: name }] },
        //     stateNode
        //   );
        // }
    }, [insert, notifyFormEdit, updateSectionNames]);
    const getUniqueStateNodeName = useCallback((name, stateNode) => {
        var _a, _b;
        if (!stateNode)
            stateNode = trackChanges.final;
        if (stateNode.name.trim() === name.trim()) {
            let count = +(((_b = (_a = name.match(/\([\d]+\)$/)) === null || _a === void 0 ? void 0 : _a[0].match(/\d+/)) === null || _b === void 0 ? void 0 : _b[0]) || 0);
            name = name.replace(/\([\d]+\)$/, '').trim();
            name += ` (${count + 1})`;
            // recheck from beginning
            return getUniqueStateNodeName(name, trackChanges.final);
        }
        else if (stateNode.states) {
            for (const childStateNode of stateNode.states) {
                const newName = getUniqueStateNodeName(name, childStateNode);
                if (newName !== name) {
                    // if new name is returned then name is unique
                    return newName;
                }
            }
        }
        return name;
    }, [trackChanges.final]);
    /**
     * Update a section state node data
     */
    const updateSection = useCallback((stateNodeConfig, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        if (stateNodeConfig.name) {
            stateNodeConfig.name = getUniqueStateNodeName(stateNodeConfig.name);
            updateAllEventTargets(stateNode.name, stateNodeConfig.name);
        }
        const keys = Object.keys(stateNodeConfig);
        for (const key of keys) {
            update(`${fullPath}.${key}`, stateNodeConfig[key]);
        }
        notifyFormEdit();
    }, [
        formState.selectedStateNode,
        getStateNodePath,
        getUniqueStateNodeName,
        notifyFormEdit,
        update,
        updateAllEventTargets
    ]);
    /**
     * Update a section's meta data
     */
    const updateSectionMeta = useCallback((sectionMeta, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        // TODO: check UpdateSection.Title and rename stateNode.name
        // TODO: check for name conflict (names must be unique for state transitions to work)
        if (sectionMeta.title) {
            const name = getUniqueStateNodeName(sectionMeta.title);
            updateAllEventTargets(stateNode.name, name);
            update(`${fullPath}.name`, name);
        }
        const keys = Object.keys(sectionMeta);
        for (const key of keys) {
            update(`${fullPath}.meta.${key}`, sectionMeta[key]);
        }
        notifyFormEdit();
    }, [
        formState.selectedStateNode,
        getStateNodePath,
        getUniqueStateNodeName,
        notifyFormEdit,
        update,
        updateAllEventTargets
    ]);
    /**
     * Reorder a section
     */
    const moveSection = useCallback((sourceIndex, destinationIndex) => {
        move('states', sourceIndex, destinationIndex);
        updateSectionNames();
        notifyFormEdit();
    }, [move, notifyFormEdit, updateSectionNames]);
    /**
     * Delete a section
     */
    const deleteSection = useCallback((index) => {
        remove(`states[${index}]`);
        notifyFormEdit();
    }, [notifyFormEdit, remove]);
    /**
     * Add an entry action for a section. If no section state
     * node provided, then use selected state node.
     */
    const addSectionAction = useCallback((action, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        if (!stateNode.onEntry) {
            update(`${fullPath}.onEntry`, []);
        }
        insert(`${fullPath}.onEntry`, action);
        notifyFormEdit();
    }, [
        formState.selectedStateNode,
        getStateNodePath,
        insert,
        update,
        notifyFormEdit
    ]);
    /**
     * Update an entry action for a section. If no section state
     * node provided, then use selected state node.
     */
    const updateSectionAction = useCallback((index, action, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        update(`${fullPath}.onEntry[${index}]`, action);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, update]);
    /**
     * Reorder an entry action. If no section state node provided,
     * then use selected state node.
     */
    const moveSectionAction = useCallback((sourceIndex, destinationIndex, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        move(`${fullPath}.onEntry`, sourceIndex, destinationIndex);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, move, notifyFormEdit]);
    /**
     * Delete a section entry action
     */
    const deleteSectionAction = useCallback((index, stateNode) => {
        if (!stateNode)
            stateNode = formState.selectedStateNode;
        const fullPath = getStateNodePath(stateNode);
        remove(`${fullPath}.onEntry[${index}]`);
        notifyFormEdit();
    }, [formState.selectedStateNode, getStateNodePath, notifyFormEdit, remove]);
    return {
        getStateNodePath,
        addSection,
        updateSection,
        updateSectionMeta,
        moveSection,
        deleteSection,
        addSectionAction,
        updateSectionAction,
        moveSectionAction,
        deleteSectionAction,
        addEvent,
        updateEvent,
        moveEvent,
        deleteEvent,
        addTransition,
        updateTransition,
        moveTransition,
        deleteTransition,
        addTransitionCondition,
        updateTransitionCondition,
        moveTransitionCondition,
        deleteTransitionCondition
    };
}
//# sourceMappingURL=FormSection.hooks.js.map